import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, ShieldCheck, Mail, Sparkles, TrendingUp, Gauge, CalendarClock, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { StatCard, PnL, money, fmt, PageHeader, SkeletonGrid, EmptyState, Table, Td, StatusBadge } from "../../components/dashboard/Shared";
import { EquityChart, MonthlyBars } from "../../components/dashboard/Charts";
import LiveTradingChart from "../../components/dashboard/LiveTradingChart";
import { useAuth } from "../../context/AuthContext";

const money0 = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

// Eligibility status — declared equity vs the $1,000 minimum (broker-verified once connected).
// Phase 6 — How ChartzFx Works (paid XAUUSD copy-trading model, matching backend enforcement).
const HowChartzFxWorks = () => {
  const rows = [
    { icon: TrendingUp, title: "XAUUSD-focused copy trading", body: "Our manager trades Gold (XAUUSD) from a central account. Approved trades mirror to your connected MT5 account — you never place or modify trades yourself." },
    { icon: ShieldCheck, title: "Protective stop-loss on every trade", body: "Each new position carries a mandatory stop-loss within a capped distance. Investor-side lot and risk settings are disabled — risk is managed centrally." },
    { icon: Gauge, title: "Daily trade limit", body: "A maximum of 3 new trades per trading day per account, enforced on our servers to keep exposure controlled." },
    { icon: Sparkles, title: "50 / 50 eligible-net-profit share", body: "You keep 50% of eligible net profit; ChartzFx keeps 50%. Profit sharing applies only to net gains — never to losses." },
    { icon: CalendarClock, title: "Friday settlement", body: "Eligible profit is reconciled and settled weekly on Friday, based on your broker-verified equity." },
  ];
  return (
    <div className="mb-6 card-dark p-5" data-testid="how-it-works-card">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles size={16} className="text-[#00F0FF]" />
        <p className="label-xs">How ChartzFx Works</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {rows.map((r, i) => (
          <div key={i} className="flex items-start gap-3" data-testid={`how-it-works-item-${i}`}>
            <r.icon size={18} className="text-[#7000FF] shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-white font-medium">{r.title}</p>
              <p className="text-xs text-gray-500 leading-relaxed mt-0.5">{r.body}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-white/10 flex items-start gap-2" data-testid="how-it-works-risk">
        <AlertTriangle size={14} className="text-[#FFD600] shrink-0 mt-0.5" />
        <p className="text-[11px] text-gray-500 leading-relaxed">Trading leveraged instruments such as XAUUSD carries a significant risk of loss. ChartzFx does not guarantee profits, weekly returns, or protection of your capital. Past performance does not indicate future results. Only trade with capital you can afford to lose.</p>
      </div>
    </div>
  );
};

function EmailVerifiedBadge() {
  const { user } = useAuth();
  const [sent, setSent] = useState(false);
  if (!user) return null;
  const verified = user.email_verified;
  if (verified) {
    return (
      <div className="mb-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00E676]/10 border border-[#00E676]/30 text-[#00E676] text-xs" data-testid="email-verified-badge">
        <ShieldCheck size={12} /> Verified Email
      </div>
    );
  }
  const resend = async () => {
    try {
      await api.post("/auth/otp/resend", { email: user.email, purpose: "register" });
      setSent(true);
      toast.success("Verification code sent — check your inbox");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send code");
    }
  };
  return (
    <div className="mb-6 card-dark border-yellow-500/40 p-3 flex items-center justify-between" data-testid="email-not-verified-banner">
      <div className="flex items-center gap-2 text-yellow-100 text-sm">
        <Mail size={16} className="text-yellow-400" /> Email Not Verified
      </div>
      <button onClick={resend} disabled={sent} className="btn-primary text-xs px-4 py-1.5" data-testid="verify-now-btn">
        {sent ? "Code sent" : "Verify Now"}
      </button>
    </div>
  );
}

const RANGES = [
  ["", "All time"], ["today", "Today"], ["yesterday", "Yesterday"], ["7d", "Last 7 days"],
  ["30d", "Last 30 days"], ["month", "This month"], ["last_month", "Last month"],
  ["year", "This year"], ["custom", "Custom"],
];

export default function InvestorDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [live, setLive] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [range, setRange] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const userPickedRef = useRef(false);

  const pickSymbol = useCallback((sym) => {
    userPickedRef.current = true;
    setSelectedSymbol(sym);
  }, []);

  const loadDashboard = useCallback(async (opts = {}) => {
    try {
      const params = { ...opts };
      if (range) params.range = range;
      if (range === "custom" && start && end) { params.start = start; params.end = end; }
      const { data } = await api.get("/investor/dashboard", { params });
      setData(data);
      setError(false);
    } catch {
      setError(true);
    }
  }, [range, start, end]);

  const loadLive = useCallback(async () => {
    try {
      const { data } = await api.get("/investor/positions");
      setLive(data);
    } catch { /* stale */ }
  }, []);

  useEffect(() => {
    // First load includes a live MetaApi sync; live positions fire in parallel for instant fill.
    loadDashboard({ sync: true });
    loadLive();
  }, [loadDashboard, loadLive]);

  // Re-fetch dashboard when the range filter changes (without a live sync — just re-derive metrics).
  // NOTE: `data` intentionally excluded from deps to prevent infinite refetch loop (setData -> effect -> setData).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadDashboard({ sync: false }); }, [range, start, end]);

  useEffect(() => {
    if (!data?.connected) return;
    const t = setInterval(loadLive, 5000);
    return () => clearInterval(t);
  }, [data?.connected, loadLive]);

  // Auto-select the chart symbol once: first open position's symbol, else XAUUSD.
  // Never force-switch after the user manually picks a symbol.
  useEffect(() => {
    if (userPickedRef.current || selectedSymbol || !live) return;
    const first = (live.positions || [])[0]?.symbol;
    setSelectedSymbol(first || "XAUUSD");
  }, [live, selectedSymbol]);

  const manualSync = async () => {
    setSyncing(true);
    await Promise.all([loadDashboard({ sync: true }), loadLive()]);
    setSyncing(false);
  };

  if (error) return <EmptyState title="Failed to load dashboard" sub="Please refresh the page or try again later." />;
  if (!data) return <div className="space-y-6"><div className="skeleton h-10 w-64" /><SkeletonGrid /><div className="skeleton h-72" /></div>;

  if (!data.connected) {
    if (data.status === "connecting")
      return (
        <div>
          <PageHeader title="Dashboard" sub="Waiting for live MetaApi data" />
          <EmptyState
            title="Your MT5 account is being connected…"
            sub={`Current step: ${data.stage || "connecting"} — no data is shown until synchronization with your broker is complete.`}
            action={<Link data-testid="view-connection-progress" to="/investor/mt5" className="btn-primary inline-flex px-6 py-2.5 mt-5 text-sm">View Connection Progress</Link>}
          />
        </div>
      );
    if (data.status === "failed")
      return (
        <div>
          <PageHeader title="Dashboard" sub="Connection problem" />
          <EmptyState
            title="MetaApi connection failed"
            sub={data.last_error || "See the MT5 Account page for the exact error."}
            action={<Link data-testid="fix-connection-cta" to="/investor/mt5" className="btn-primary inline-flex px-6 py-2.5 mt-5 text-sm">Fix Connection</Link>}
          />
        </div>
      );
    return (
      <div>
        <PageHeader title="Dashboard" sub="Your trading performance at a glance" />
        <HowChartzFxWorks />
        <EmptyState
          title="No MT5 account connected yet"
          sub="Connect your broker account — data appears only after live synchronization via MetaApi."
          action={<Link data-testid="connect-mt5-cta" to="/investor/mt5" className="btn-primary inline-flex px-6 py-2.5 mt-5 text-sm">Connect MT5 Account</Link>}
        />
      </div>
    );
  }

  const m = data.metrics;
  // Prefer live values when available (account payload from /investor/positions)
  const livePos = live?.positions || [];
  const floatingPnl = livePos.reduce((s, p) => s + (p.profit || 0), 0);
  const balance = live?.account?.balance ?? m.balance;
  const equity = live?.account?.equity ?? m.equity;
  const freeMargin = live?.account?.free_margin ?? m.free_margin;
  const openCount = livePos.length || m.open_trades_count || 0;
  const closedCount = m.closed_trades ?? 0;

  // Chart symbol logic: options = open-position symbols + XAUUSD; active = selected (or first position / XAUUSD).
  const symbolOptions = [...new Set(livePos.map((p) => p.symbol).concat("XAUUSD"))];
  const activeSymbol = selectedSymbol || livePos[0]?.symbol || "XAUUSD";
  const selectedPosition = livePos.find((p) => p.symbol === activeSymbol) || null;

  return (
    <div>
      <PageHeader title="Dashboard" sub={`${data.account.broker} · ${data.account.login} · ${data.account.server}`}>
        <div className="flex items-center gap-3 flex-wrap">
          <select data-testid="dashboard-range-select" value={range} onChange={(e) => { setRange(e.target.value); }} className="input-dark !w-44 !py-1.5 text-xs">
            {RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {range === "custom" && (
            <>
              <input data-testid="dashboard-range-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="input-dark !w-40 !py-1.5 text-xs" />
              <input data-testid="dashboard-range-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="input-dark !w-40 !py-1.5 text-xs" />
            </>
          )}
          <span className="label-xs">Trading</span>
          <StatusBadge status={data.trading_status === "active" ? "active" : "paused"} />
          <button
            data-testid="dashboard-sync-button"
            onClick={manualSync}
            disabled={syncing}
            className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw size={12} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Refresh"}
          </button>
        </div>
      </PageHeader>

      {data.sync_error && (
        <div className="card-dark border-[#FFD600]/40 p-4 mb-6 text-xs text-[#FFD600] flex items-start gap-3" data-testid="dashboard-sync-error">
          <span>Live sync warning: {String(data.sync_error).slice(0, 200)} — showing last synced snapshot.</span>
        </div>
      )}

      <EmailVerifiedBadge />

      {/* How the paid XAUUSD copy-trading model works */}
      <HowChartzFxWorks />

      {/* ACCOUNT SUMMARY — compact 2-col on mobile */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 mb-6">
        <StatCard testId="metric-balance" label="Balance" value={money(balance)} delay={0} />
        <StatCard testId="metric-equity" label="Equity" value={money(equity)} accent="text-[#00F0FF]" delay={60} />
        <StatCard testId="metric-free-margin" label="Free Margin" value={money(freeMargin)} delay={120} />
        <StatCard testId="metric-floating-pnl" label="Floating P&L" value={<PnL value={floatingPnl} />} delay={180} />
        <StatCard testId="metric-open-trades" label="Open Trades" value={openCount} accent={openCount > 0 ? "text-[#00E676]" : "text-white"} delay={240} />
        <StatCard testId="metric-closed-trades" label="Closed Trades" value={closedCount} delay={300} />
      </div>

      {/* MAIN TRADING AREA — view-only live MT5-style chart */}
      <div className="mb-6" data-testid="dashboard-chart-section">
        <LiveTradingChart
          symbol={activeSymbol}
          symbols={symbolOptions}
          position={selectedPosition}
          connected={data.connected}
          onSelectSymbol={pickSymbol}
        />
      </div>

      {/* OPEN POSITIONS — cards on mobile, table on desktop */}
      <div className="card-dark p-4 sm:p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <p className="label-xs">Open Positions — Live ({livePos.length})</p>
          <Link data-testid="view-all-positions-link" to="/investor/positions" className="text-xs text-[#00F0FF] hover:underline">Manage →</Link>
        </div>
        {!live ? (
          <div className="skeleton h-24" />
        ) : livePos.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">No open positions right now.</p>
        ) : (
          <>
            {/* Mobile position cards */}
            <div className="lg:hidden space-y-3">
              {livePos.map((t) => (
                <button
                  key={t.id}
                  onClick={() => pickSymbol(t.symbol)}
                  data-testid={`position-card-${t.id}`}
                  className={`w-full text-left rounded-xl border p-4 transition-colors ${activeSymbol === t.symbol ? "border-[#7000FF]/50 bg-[#7000FF]/10" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="tabular text-white text-base font-medium">{t.symbol}</p>
                      <p className={`text-xs mt-0.5 ${t.type === "buy" ? "text-[#00E676]" : "text-[#FF3B30]"}`}>{t.type.toUpperCase()} · {fmt(t.volume)} LOT</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">P&amp;L</p>
                      <PnL value={t.profit} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                    <div className="flex justify-between"><span className="text-gray-500">Entry</span><span className="tabular text-gray-200">{t.open_price}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Current</span><span className="tabular text-[#00F0FF]">{t.current_price ?? "—"}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">SL</span><span className="tabular text-[#FF3B30]">{t.sl ?? "—"}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">TP</span><span className="tabular text-[#00E676]">{t.tp ?? "—"}</span></div>
                  </div>
                </button>
              ))}
            </div>
            {/* Desktop table */}
            <div className="hidden lg:block overflow-x-auto -mx-2 px-2">
              <Table headers={["Symbol", "Type", "Lots", "Open Price", "Current", "SL", "TP", "Floating P&L"]} testId="open-trades-table">
                {livePos.map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => pickSymbol(t.symbol)}
                    data-testid={`dashboard-position-row-${t.id}`}
                    className={`border-b border-white/5 cursor-pointer transition-colors ${activeSymbol === t.symbol ? "bg-[#7000FF]/10" : "hover:bg-white/[0.03]"}`}
                  >
                    <Td className="tabular text-white">{t.symbol}</Td>
                    <Td><span className={t.type === "buy" ? "text-[#00E676]" : "text-[#FF3B30]"}>{t.type.toUpperCase()}</span></Td>
                    <Td className="tabular">{fmt(t.volume)}</Td>
                    <Td className="tabular">{t.open_price}</Td>
                    <Td className="tabular text-gray-400">{t.current_price ?? "—"}</Td>
                    <Td className="tabular text-xs text-[#FF3B30]">{t.sl ?? "—"}</Td>
                    <Td className="tabular text-xs text-[#00E676]">{t.tp ?? "—"}</Td>
                    <Td><PnL value={t.profit} /></Td>
                  </tr>
                ))}
              </Table>
            </div>
            <p className="text-[10px] text-gray-600 mt-3">Tap a position to focus the chart on that symbol.</p>
          </>
        )}
      </div>

      {/* PERFORMANCE METRICS (secondary) */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-6">
        <StatCard testId="metric-daily-pnl" label="Daily P&L (incl. floating)" value={<PnL value={m.daily_pnl} />} delay={0} />
        <StatCard testId="metric-monthly-return" label="Monthly Return" value={`${m.monthly_return >= 0 ? "+" : ""}${fmt(m.monthly_return)}%`} accent={m.monthly_return >= 0 ? "text-[#00E676]" : "text-[#FF3B30]"} delay={60} />
        <StatCard testId="metric-drawdown" label="Max Drawdown" value={`${fmt(m.max_drawdown)}%`} accent="text-[#FFD600]" delay={120} />
      </div>

      {data.range && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6" data-testid="range-stats-row">
          <StatCard testId="range-profit" label={`Range Profit (${range})`} value={<PnL value={m.range_profit || 0} />} accent="text-[#00F0FF]" />
          <StatCard testId="range-trades" label="Range Trades" value={m.range_trades ?? 0} />
          <StatCard testId="range-win-rate" label="Range Win Rate" value={`${fmt(m.range_win_rate || 0)}%`} accent={m.range_win_rate >= 50 ? "text-[#00E676]" : "text-[#FFD600]"} />
          <StatCard testId="range-days" label="Days in Range" value={data.range.start && data.range.end ? Math.max(1, Math.round((new Date(data.range.end) - new Date(data.range.start)) / 86400000)) : "—"} />
        </div>
      )}

      {/* CHARTS (equity + monthly) */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="card-dark p-5 lg:col-span-2">
          <p className="label-xs mb-4">Equity Curve — 6 Months</p>
          <EquityChart data={data.equity_curve} />
        </div>
        <div className="card-dark p-5">
          <p className="label-xs mb-4">Monthly Returns %</p>
          <MonthlyBars data={data.monthly_returns} height={280} />
        </div>
      </div>
    </div>
  );
}
